import { useEffect, useRef, useState } from 'react'
import { BottomSheet } from './BottomSheet'
import { Button } from './Button'
import { useNls } from '../../nls/nlsProvider'
import { requestWorkspaceSwitch } from '../../services/workspaceSwitchService'
import {
  registerWorkspaceAccessLostPrompt,
  type WorkspaceAccessLostPromptRequest,
} from '../../services/workspaceSwitch/accessLostPrompt'

/**
 * Shared sheet displayed when the user loses access to their active workspace
 * (removed/revoked membership or workspace deletion).
 * Displays:
 * - "You no longer have access to {name}" or "{name} was deleted"
 * - One OK button that triggers MEMBERSHIP_ENDED switch to next workspace.
 */
export const WorkspaceAccessLostSheet = () => {
  const { t } = useNls()
  const [request, setRequest] = useState<WorkspaceAccessLostPromptRequest | null>(null)
  const [isBusy, setIsBusy] = useState(false)
  const requestRef = useRef<WorkspaceAccessLostPromptRequest | null>(null)
  useEffect(() => {
    requestRef.current = request
  }, [request])

  useEffect(() => {
    const unregister = registerWorkspaceAccessLostPrompt((req) => {
      requestRef.current = req
      setRequest(req)
    })
    return () => {
      unregister()
      if (requestRef.current) {
        const pending = requestRef.current
        requestRef.current = null
        void requestWorkspaceSwitch({
          source: 'MEMBERSHIP_ENDED',
          toWorkspaceId: pending.toWorkspaceId,
          landing: pending.landing,
        })
      }
    }
  }, [])

  if (!request) return null

  const title =
    request.reason === 'deleted'
      ? t('NLSWorkspaceDeletedNotice', { name: request.workspaceName })
      : t('NLSWorkspaceAccessLostNotice', { name: request.workspaceName })

  const handleConfirm = async () => {
    if (isBusy) return
    setIsBusy(true)
    const currentReq = request
    try {
      if (currentReq) {
        requestRef.current = null
        await requestWorkspaceSwitch({
          source: 'MEMBERSHIP_ENDED',
          toWorkspaceId: currentReq.toWorkspaceId,
          landing: currentReq.landing,
        })
      }
      setRequest(null)
    } finally {
      setIsBusy(false)
    }
  }

  return (
    <BottomSheet
      isOpen={Boolean(request)}
      onClose={handleConfirm}
      title={title}
      className="memberBottomSheet workspaceAccessLostSheet"
      maxHeightSvh={40}
    >
      <div
        className="p-4 flex flex-col gap-4 text-[var(--text-strong)]"
        data-testid="workspace-access-lost-sheet"
      >
        <div className="flex items-center justify-end pt-2">
          <Button
            variant="primary"
            size="md"
            disabled={isBusy}
            onClick={handleConfirm}
            data-testid="workspace-access-lost-ok"
          >
            {t('NLSWorkspaceAccessLostOk')}
          </Button>
        </div>
      </div>
    </BottomSheet>
  )
}
